import { z } from 'zod'

import { calendarDate, type DateFormat } from '../lib/date'
import { NotFoundError, ValidationError } from '../lib/errors'
import {
  DEFAULT_TIMEZONE,
  formatRegionPrice,
  REGION_IDS,
  type Region,
  type RegionId,
  regionIdSchema,
  requireRegion,
} from './region'

export const CLINIC_IDS = {
  BALHAM: 'balham',
  ISLINGTON: 'islington',
  ACTON: 'acton',
  LONDON_FIELDS: 'london-fields',
  FOREST_HILL: 'forest-hill',
  BOUNDS_GREEN: 'bounds-green',
  CLIFTON: 'clifton',
} as const

export const clinicIdSchema = z.enum(Object.values(CLINIC_IDS))
export type ClinicId = z.infer<typeof clinicIdSchema>

export const clinicSchema = z.object({
  /** Slug used as the primary key everywhere (e.g. "balham", "london-fields"). */
  id: clinicIdSchema,
  /** Region this clinic belongs to — drives currency, units, and tax behaviour. */
  region: regionIdSchema,
  name: z.string(),
  address: z.string(),
  timezone: z.string(),
  googlePlaceId: z.string(),
  /** Public contact number in E.164. Every UK clinic currently shares one line. */
  phone: z.string(),
  email: z.string().optional(),
  /** Clinic-specific context shown to members when they book an appointment. */
  additionalBookingInfo: z.string().optional(),
  /** When set, the clinic is no longer available for new operational activity. */
  archivedAt: calendarDate.nullable(),
})
export type Clinic = z.output<typeof clinicSchema>

export const CLINICS = {
  BALHAM: {
    id: CLINIC_IDS.BALHAM,
    region: REGION_IDS.UK,
    name: 'Balham',
    address: '9 Fernlea Rd, London SW12 9RT, United Kingdom',
    timezone: 'Europe/London',
    googlePlaceId: 'ChIJIeBt44wFdkgRyvlqA4sxOYo',
    phone: '+447458699020',
    archivedAt: '2026-09-25',
  },
  ISLINGTON: {
    id: CLINIC_IDS.ISLINGTON,
    region: REGION_IDS.UK,
    name: 'Islington',
    address: '6 Camden Walk, London N1 8DY, United Kingdom',
    timezone: 'Europe/London',
    googlePlaceId: 'ChIJc7zbWd8bdkgRP0nVZwAFMDY',
    phone: '+447458699020',
    archivedAt: null,
  },
  ACTON: {
    id: CLINIC_IDS.ACTON,
    region: REGION_IDS.UK,
    name: 'Acton',
    address: '45 Churchfield Rd, London W3 6AY, United Kingdom',
    timezone: 'Europe/London',
    googlePlaceId: 'ChIJVYrOQQsPdkgR-ZMtzolxIz4',
    phone: '+447458699020',
    archivedAt: null,
  },
  LONDON_FIELDS: {
    id: CLINIC_IDS.LONDON_FIELDS,
    region: REGION_IDS.UK,
    name: 'London Fields',
    address: '143 Mare St, London E8 3RH, United Kingdom',
    timezone: 'Europe/London',
    googlePlaceId: 'ChIJ1Q_cyaQddkgRjZdXoI_usCY',
    phone: '+447458699020',
    archivedAt: null,
  },
  FOREST_HILL: {
    id: CLINIC_IDS.FOREST_HILL,
    region: REGION_IDS.UK,
    name: 'Forest Hill',
    address: '13 Dartmouth Rd, London SE23 3HN, United Kingdom',
    timezone: 'Europe/London',
    googlePlaceId: 'ChIJl2CAc9EDdkgRZfPT9iZOQos',
    phone: '+447458699020',
    archivedAt: '2026-09-25',
  },
  BOUNDS_GREEN: {
    id: CLINIC_IDS.BOUNDS_GREEN,
    region: REGION_IDS.UK,
    name: 'Bounds Green',
    address: '2 Gateway Mews, London N11 2UT, United Kingdom',
    timezone: 'Europe/London',
    googlePlaceId: 'ChIJYaqGyhMZdkgRHS5Ww3aKInY',
    phone: '+447458699020',
    additionalBookingInfo:
      'If the outside gate to the mews is closed, use gate code 1966 to enter.',
    archivedAt: null,
  },
  CLIFTON: {
    id: CLINIC_IDS.CLIFTON,
    region: REGION_IDS.US,
    name: 'Clifton',
    address: 'Unit 133, The Shops at the Promenade, 852 Route 3, Clifton, NJ 07012',
    timezone: 'America/New_York',
    googlePlaceId: 'ChIJ8XvUNLP_wokRAOhuDvo6u9o',
    phone: '+19735534401',
    email: 'clifton@snootsvet.com',
    archivedAt: null,
  },
} satisfies Record<string, Clinic>

/** Lookup map from clinic slug ID to clinic object — O(1) access by ClinicId. */
export const CLINICS_BY_ID: Record<ClinicId, Clinic> = Object.fromEntries(
  Object.values(CLINICS).map((c) => [c.id, c])
) as Record<ClinicId, Clinic>

export const activeClinicIdSchema = clinicIdSchema.refine(
  (id) => CLINICS_BY_ID[id].archivedAt === null,
  { message: 'Clinic is closed' }
)

/** Returns clinics sorted alphabetically by name, excluding archived clinics by default. */
export function getClinics({
  includeArchived = false,
}: {
  includeArchived?: boolean
} = {}): Clinic[] {
  return Object.values(CLINICS_BY_ID)
    .filter((clinic) => includeArchived || clinic.archivedAt === null)
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Returns the clinic with the given slug ID, or null if not found. */
export function getClinic(id?: string | null): Clinic | null {
  if (!id) return null
  return CLINICS_BY_ID[id as ClinicId] ?? null
}

/** Returns the IANA timezone for the clinic, falling back to the UK default. */
export function getClinicTimezone(id: string): string {
  return getClinic(id)?.timezone ?? DEFAULT_TIMEZONE
}

/** Returns the clinic with the given slug ID, or throws NotFoundError if not found. */
export function requireClinic(id: string): Clinic {
  const clinic = CLINICS_BY_ID[id as ClinicId]
  if (!clinic) throw new NotFoundError(`Clinic '${id}' not found`)
  return clinic
}

export function getClinicMapsUrl(
  clinic: Pick<Clinic, 'name' | 'address' | 'googlePlaceId'>
): string {
  const params = new URLSearchParams({
    api: '1',
    query: `${clinic.name}, ${clinic.address}`,
    query_place_id: clinic.googlePlaceId,
  })
  return `https://www.google.com/maps/search/?${params}`
}

export function requireActiveClinic(id: string): Clinic {
  const clinic = requireClinic(id)
  if (clinic.archivedAt) throw new ValidationError(`${clinic.name} clinic is closed`)
  return clinic
}

/** Snoots headquarters — used in PDF exports and other business-facing documents. */
export const SNOOTS_HQ = {
  name: 'Snoots',
  address: '6 Camden Walk, N1 8DY',
  phone: '07547 119234',
  whatsappUrl: 'https://wa.me/447547119234',
  email: 'boop@snootsvet.com',
  website: 'www.snootsvet.com',
} as const

/**
 * Snoots Bounds Green — used on documents for procedures carried out at that
 * clinic (dental and sedation letters, consents and discharge notes). Contact
 * details mirror Snoots HQ for now but are kept separate so they can diverge
 * (email/phone/etc.) in the future.
 */
export const SNOOTS_BG = {
  name: SNOOTS_HQ.name,
  address: CLINICS.BOUNDS_GREEN.address,
  phone: SNOOTS_HQ.phone,
  email: SNOOTS_HQ.email,
  website: SNOOTS_HQ.website,
} as const

/**
 * Snoots US (Clifton, NJ) — used on US-specific documents such as the rabies
 * vaccination certificate. Contact email/website mirror Snoots HQ.
 */
export const SNOOTS_US = {
  name: 'Snoots Clifton',
  address: CLINICS.CLIFTON.address,
  phone: '+1 (973) 553-4401',
  textPhone: '+1 (862) 238-0529',
  email: 'boops@joinsnoots.com',
  website: 'joinsnoots.com',
} as const

/** Veteris out-of-hours emergency contact. */
export const VETERIS = {
  name: 'Veteris',
  phone: '0330 808 9066',
} as const

export function getRegionDefaultClinic(regionId: RegionId) {
  const defaults = { uk: 'islington', us: 'clifton' } as const
  return requireClinic(defaults[regionId])
}

/** Derives the region for a clinic slug. Throws NotFoundError if the clinic is unknown. */
export function getClinicRegion(clinicId: string): Region {
  return requireRegion(requireClinic(clinicId).region)
}

/**
 * Formats a committed order/invoice amount in the currency of the clinic it was
 * billed at — e.g. an order at a UK clinic renders "£14.28" even for a viewer
 * whose active region is US. Use this for money that is already denominated
 * (orders, invoices, charges), where the clinic is the source of truth; use
 * `formatRegionPrice` with the viewer's region for prices you are setting now.
 */
export function formatClinicPrice(minorUnits: number, clinicId: string): string {
  return formatRegionPrice(minorUnits, getClinicRegion(clinicId).id)
}

/**
 * Formats a clinic's missed-appointment fee for client-facing copy — "£25" at a UK
 * clinic, "$65" at a US one.
 *
 * Not `formatClinicPrice`: a fee quoted in a sentence reads better without the
 * trailing zeros ("a £25 fee", not "a £25.00 fee"), so whole amounts drop the
 * decimals while a fee ever set to a non-round amount still shows them.
 */
export function formatClinicAppointmentPenaltyFee(clinicId: string): string {
  const region = getClinicRegion(clinicId)
  return new Intl.NumberFormat(region.locale, {
    style: 'currency',
    currency: region.currency,
    trailingZeroDisplay: 'stripIfInteger',
  }).format(region.noShowFee / 100)
}

/**
 * Everything needed to render a record's timestamps: its clinic's own zone when
 * it has one, otherwise the zone of the region that owns it, plus that region's
 * locale and clock convention.
 *
 * A record with a clinic happened somewhere, and that place fixes which moment —
 * and so which day — it was. A record without one didn't happen at a place, so
 * `fallbackRegion` decides: staff UI passes the viewer's region (see
 * `FormattedDate`/`FormattedTime`), while generated output — printed histories,
 * portal pages, emails — has no viewer and must pass the owning member's.
 *
 * An unknown clinic id falls back to the region rather than throwing, since
 * clinic ids arrive from stored data. An unknown region throws, since that is a
 * programming error.
 */
export function resolveDateFormat(
  clinicId: string | null | undefined,
  fallbackRegion: RegionId
): DateFormat {
  const clinic = clinicId ? getClinic(clinicId) : null
  const region = requireRegion(clinic ? clinic.region : fallbackRegion)
  return {
    timeZone: clinic ? clinic.timezone : region.timezone,
    locale: region.locale,
    hour12: region.hour12,
  }
}
